import {
  useEffect, useCallback, useState, useRef, useMemo,
} from 'react';
import { message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useHistory } from 'react-router';

import businessTypeAction from '@state/businessType/actions';
import devicesApi from '@api/devicesApi';
import getColumns from './columnBusinessType';

function useBusinessType() {

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const abortController = useRef(null);
  const timerLopping = useRef();
  const initPagination = useRef({ current: 1, pageSize: 20 });
  const history = useHistory();
  const [valueFilter, setValueFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [businessType, setBusinessType] = useState([]);
  const [pag, setPagination] = useState(initPagination.current);

  const [isCreate, setIsCreate] = useState(false);
  const currentUser = useSelector(({ user }) => user);

  const abortLastRequest = useCallback(() => {

    if (abortController.current) {

      abortController.current.abort();
      abortController.current = null;

    }

  }, []);

  const handleTableChange = useCallback( // load BusinessType data
    (pagination, filter, sorter) => {

      abortController.current = new AbortController();
      const { signal } = abortController.current;

      setLoading(true);
      if (timerLopping.current) clearTimeout(timerLopping.current);

      devicesApi
        .getAllBusinessType({
          signal,
        })
        .then((response) => {

          const resp = {
            count: response.length,
            results: response,
          };
          // eslint-disable-next-line no-shadow
          const { results: business_type, count: total } = resp;

          setPagination({ ...pagination, total });
          setLoading(false);

          const newBusinessType = business_type.map((item) => {

            const devices_associated = item?.devicesList?.length;
            const communities_associated = item?.clientsList?.length;

            return { ...item, devices_associated, communities_associated };

          });

          setBusinessType(newBusinessType);

          timerLopping.current = setTimeout(
            () => handleTableChange({ pagination, filter, sorter }),
            120000,
          );

        })
        .catch((resError) => {

          if (signal.aborted) return;
          const { fem } = resError;
          // eslint-disable-next-line no-console
          console.log(t(fem));
          setLoading(false);

        });

    },
    [t],
  );

  const createBusinessType = useCallback( // Go to create
    () => {

      dispatch(businessTypeAction.setBusinessTypeIdEdit(null));
      history.push('/dispositivos/negocio/crear-negocio');

    },
    [history, dispatch],
  );

  const deleteBusinessType = useCallback((e) => { // Delete BusinessType by Id

    const disable = e && e.devicesList && e.devicesList.length;

    if (disable) {

      message.error(t('common.feedbacks.devices_associated_failed'));
      return;

    }

    abortController.current = new AbortController();
    const { signal } = abortController.current;
    const { id } = e;
    devicesApi.deleteBusinessType({ signal, id })
      .then((response) => {

        handleTableChange(initPagination.current);
        message.success(t('common.successfully_remove'));

      })
      .catch((resError) => {

        // eslint-disable-next-line
         console.log(resError); 
        message.error(resError?.data?.detail || t('common.failed_remove'));

      });

  }, [handleTableChange, t]);

  // eslint-disable-next-line no-console
  const editBusinessType = useCallback((e) => { // edit BusinessType

    const defaultData = {
      id: e.id,
      name: e.name,
      description: e.description,
      latitude: e.latitude,
      longitude: e.longitude,
      type: e.type,
      community_id: e?.ext_community?.id,
      type_communitie: e?.ext_community?.type,
      settlement_cycle: e?.ext_community?.settlement_cycle,
      frt: e?.ext_community?.frt,
    };
    dispatch(businessTypeAction.setBusinessTypeIdEdit(e.id));
    dispatch(businessTypeAction.setCreateEditBusinessType(defaultData));
    history.push('/dispositivos/negocio/tipo-negocio');

  }, [dispatch, history]);

  const columns = useMemo(
    () => getColumns(
      t,
      deleteBusinessType,
      currentUser,
      editBusinessType,
    ), [
      t,
      deleteBusinessType,
      currentUser,
      editBusinessType,
    ],
  );

  const arrayFilter = businessType.filter((data) => ( // Filter by name
    data.name.toUpperCase().includes(valueFilter.toUpperCase())
  ));

  const getPermissonCreate = useCallback(() => {

      const currentUserRole = currentUser.role;
      // eslint-disable-next-line
      setIsCreate(!!(currentUserRole === 'Superadmin'));

  }, []);

  useEffect(() => {

    getPermissonCreate();

    handleTableChange(initPagination.current);
    return abortLastRequest;

  }, []);

  return {
    setValueFilter,
    handleTableChange,
    loading,
    arrayFilter,
    businessType,
    columns,
    pagination: pag,
    createBusinessType,
    isCreate,
  };

}

export default useBusinessType;
